/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Alert, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import StandardLogin from '../../components/standard-login'
import PasswordlessLogin from '@salesforce/retail-react-app/app/components/passwordless-login'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {loginFormStyle, styleAccountBenefits} from './index.style'
import ContentRenderer from '../content-renderer'

const LoginForm = ({
    hidePassword,
    hideEmail,
    submitForm,
    handleEmailSubmit,
    isCustomerSearchLoading,
    handleForgotPasswordClick,
    handlePasswordlessLoginClick,
    // clickCreateAccount = noop,
    form,
    isPasswordlessEnabled = false,
    isSocialEnabled = false,
    idps = [],
    setLoginType
}) => {
    return (
        <Fragment>
            <Stack justify="center" align="center" spacing={5}>
                <Text
                    fontSize="22px"
                    fontStyle="normal"
                    fontWeight="semibold"
                    lineHeight="normal"
                    textTransform="uppercase"
                    letterSpacing="3px"
                    mb={0}
                >
                    <FormattedMessage
                        defaultMessage="My Account"
                        id="login_form.title.my_account"
                    />
                </Text>
                <Text
                    fontSize="md"
                    lineHeight="normal"
                    letterSpacing="1px"
                    mt={0}
                    textAlign="center"
                >
                    <FormattedMessage
                        defaultMessage="Sign-up for My Cavender's and receive 10% off your next purchase."
                        id="login_form.description.offer"
                    />
                </Text>
            </Stack>
            <Stack justify="center" align="center" spacing={10} my={45}>
                <ContentRenderer
                    color="white"
                    fontFamily="Montserrat"
                    contentIds="create-account-benefits"
                    sx={styleAccountBenefits}
                    isSlot={false}
                />
            </Stack>
            <form
                id="login-form"
                onSubmit={form.handleSubmit(submitForm)}
                data-testid="sf-auth-modal-form"
                noValidate
            >
                {form.formState.errors?.global && (
                    <Alert status="error">
                        <AlertIcon color="red.500" boxSize={4} />
                        <Text fontSize="sm" ml={3}>
                            {form.formState.errors.global.message}
                        </Text>
                    </Alert>
                )}
                <Stack spacing={6} sx={loginFormStyle}>
                    {isPasswordlessEnabled ? (
                        <PasswordlessLogin
                            form={form}
                            handleForgotPasswordClick={handleForgotPasswordClick}
                            handlePasswordlessLoginClick={handlePasswordlessLoginClick}
                            isSocialEnabled={isSocialEnabled}
                            idps={idps}
                            setLoginType={setLoginType}
                        />
                    ) : (
                        <StandardLogin
                            form={form}
                            handleEmailSubmit={handleEmailSubmit}
                            isCustomerSearchLoading={isCustomerSearchLoading}
                            handleForgotPasswordClick={handleForgotPasswordClick}
                            isSocialEnabled={isSocialEnabled}
                            hideEmail={hideEmail}
                            hidePassword={hidePassword}
                            idps={idps}
                        />
                    )}
                </Stack>
            </form>
        </Fragment>
    )
}

LoginForm.propTypes = {
    hideEmail: PropTypes.bool,
    hidePassword: PropTypes.bool,

    submitForm: PropTypes.func,
    handleEmailSubmit: PropTypes.func,
    isCustomerSearchLoading: PropTypes.bool,
    handleForgotPasswordClick: PropTypes.func,
    clickCreateAccount: PropTypes.func,
    handlePasswordlessLoginClick: PropTypes.func,
    form: PropTypes.object,
    isPasswordlessEnabled: PropTypes.bool,
    isSocialEnabled: PropTypes.bool,
    idps: PropTypes.arrayOf(PropTypes.string),
    setLoginType: PropTypes.func
}

export default LoginForm
